'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { BackgroundType } from '@/lib/background-processor';

type VirtualBackgroundContextValue = {
  backgroundType: BackgroundType;
  isVirtualBackgroundActive: boolean;
  setVirtualBackgroundType: (type: BackgroundType) => void;
};

const VirtualBackgroundContext = createContext<VirtualBackgroundContextValue | null>(null);

export function VirtualBackgroundProvider({ children }: { children: React.ReactNode }) {
  const [backgroundType, setBackgroundType] = useState<BackgroundType>('none');

  const setVirtualBackgroundType = useCallback((type: BackgroundType) => {
    setBackgroundType(type);
  }, []);

  const value = useMemo(
    () => ({
      backgroundType,
      isVirtualBackgroundActive: backgroundType !== 'none',
      setVirtualBackgroundType,
    }),
    [backgroundType, setVirtualBackgroundType],
  );

  return (
    <VirtualBackgroundContext.Provider value={value}>{children}</VirtualBackgroundContext.Provider>
  );
}

export function useVirtualBackground(): VirtualBackgroundContextValue {
  const ctx = useContext(VirtualBackgroundContext);
  if (!ctx) {
    return {
      backgroundType: 'none',
      isVirtualBackgroundActive: false,
      setVirtualBackgroundType: () => {},
    };
  }
  return ctx;
}

/** Sincroniza tipo de fundo a partir de callbacks fora da árvore React imediata. */
export function VirtualBackgroundSync({
  onRegister,
}: {
  onRegister: (setter: (type: BackgroundType) => void) => void;
}) {
  const { setVirtualBackgroundType } = useVirtualBackground();
  React.useEffect(() => {
    onRegister(setVirtualBackgroundType);
  }, [onRegister, setVirtualBackgroundType]);
  return null;
}
